// Acta de Entrega-Recepción (Fase 7, ADR-EMR-4 paso 2) — portada el
// 2026-09-13 desde packages/hce (HceLicenciaActaPage + ActaEntregaRecepcion)
// al retirar la app vieja. Antes el aviso de Modo Consulta mandaba a
// `/hce/licencia/acta` del legado, y desde el subdominio del consultorio esa
// ruta caía en la ficha de un "paciente" con id "hce": el médico no podía
// firmar el Acta.
//
// Documento del CONSULTORIO (no de un paciente) firmado por el profesional
// logueado: el servidor arma el PDF con /api/hce/firmar-documento (consultorio,
// nombre, matrícula y fecha los pone él) y después se registra en la bóveda
// (`registrarActaFirmada`), igual que hacía el legado. Firmarla NO detiene el
// plazo de Modo Consulta.
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { obtenerTenantSlug } from '@/lib/audit/auditedFetch';
import { useRolDelPracticante } from '@/lib/useRolDelPracticante';
import { firmarDocumentoEnServidor, registrarActaFirmada } from '@/paciente/vaultClient';

type Estado =
  | { fase: 'formulario' }
  | { fase: 'procesando'; mensaje: string }
  | { fase: 'error'; mensaje: string }
  | { fase: 'listo'; url: string; simulada: boolean };

export function LicenciaActaPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rolEstado = useRolDelPracticante();
  const matricula = rolEstado.cargando ? null : rolEstado.matricula;
  const [estado, setEstado] = useState<Estado>({ fase: 'formulario' });
  // "Ver/imprimir" (2026-09-14): ver comentario en RecetaWizard.tsx — se
  // muestra en un <iframe> dentro de la página, nunca en ventana nueva
  // (bloqueada en la mayoría de navegadores para URLs blob:).
  const [mostrarPreview, setMostrarPreview] = useState(false);
  const urlPdf = estado.fase === 'listo' ? estado.url : null;
  useEffect(() => {
    if (!urlPdf) return undefined;
    return () => URL.revokeObjectURL(urlPdf);
  }, [urlPdf]);

  async function firmar(): Promise<void> {
    if (!matricula) return;
    setEstado({ fase: 'procesando', mensaje: t('licenciaHce.acta.firmando') });
    try {
      const tenantSlug = (await obtenerTenantSlug()) ?? 'consultorio';
      const firmado = await firmarDocumentoEnServidor({
        documentoId: `acta-entrega-recepcion-${tenantSlug}-${Date.now()}`,
        tipoDocumento: 'acta-entrega-recepcion',
        especificacion: { modo: 'acta-entrega' },
      });
      setEstado({ fase: 'procesando', mensaje: t('licenciaHce.acta.registrando') });
      await registrarActaFirmada({ signatureRef: firmado.signatureRef, firmadoEn: firmado.firmadoEn });
      const blob = new Blob([firmado.signedPdf as BlobPart], { type: 'application/pdf' });
      setEstado({ fase: 'listo', url: URL.createObjectURL(blob), simulada: firmado.simulada });
    } catch (err) {
      setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <div className="min-h-screen bg-hce-paper px-4 py-10">
      <div className="mx-auto max-w-lg space-y-4 rounded-lg border border-border bg-hce-surface p-6 shadow-sm">
        <h1 className="text-lg font-medium text-hce-ink">{t('licenciaHce.acta.titulo')}</h1>

        {estado.fase === 'listo' ? (
          <>
            <Alert>
              <AlertTitle>{t('licenciaHce.acta.listoTitulo')}</AlertTitle>
              <AlertDescription>
                {t('licenciaHce.acta.listoDetalle')}
                {estado.simulada && ` ${t('licenciaHce.acta.firmaSimulada')}`}
              </AlertDescription>
            </Alert>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setMostrarPreview((v) => !v)}>
                {t('licenciaHce.acta.verImprimir')}
              </Button>
              <Button size="sm" onClick={() => void navigate('/dashboard')}>
                {t('licenciaHce.acta.volver')}
              </Button>
            </div>
            {mostrarPreview && <iframe src={estado.url} title={t('licenciaHce.acta.verImprimir')} className="h-[70vh] w-full rounded-md border border-border" />}
          </>
        ) : (
          <>
            <p className="text-sm text-hce-muted">{t('licenciaHce.acta.instruccion')}</p>
            <div>
              <label htmlFor="acta-matricula" className="mb-1 block text-xs font-mono uppercase tracking-wide text-hce-muted">
                {t('licenciaHce.acta.registroMsp')}
              </label>
              <Input id="acta-matricula" value={matricula ?? ''} readOnly placeholder={rolEstado.cargando ? t('formulario.firma.cargandoMatricula') : ''} className="max-w-xs" />
            </div>
            {!rolEstado.cargando && !matricula && (
              <Alert variant="red">
                <AlertDescription>{t('formulario.firma.sinMatricula')}</AlertDescription>
              </Alert>
            )}
            {estado.fase === 'error' && (
              <Alert variant="red">
                <AlertTitle>{t('licenciaHce.acta.error')}</AlertTitle>
                <AlertDescription>{estado.mensaje}</AlertDescription>
              </Alert>
            )}
            <Button disabled={!matricula || estado.fase === 'procesando'} onClick={() => void firmar()}>
              {estado.fase === 'procesando' ? estado.mensaje : t('licenciaHce.acta.firmarYGuardar')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
